import { DeleteOutlined, WarningFilled } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Empty,
  Flex,
  Form,
  Input,
  InputNumber,
  Segmented,
  Tag,
  Typography,
  type InputRef,
} from 'antd';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionDrawer } from '../../components/ActionDrawer';
import { LoadMore } from '../../components/LoadMore';
import { useCursorList } from '../../lib/use-cursor-list';
import { StockLocationPicker } from '../../components/StockLocationPicker';
import { ApiError } from '../../lib/api-client';
import { formatMoney } from '../../lib/money';
import {
  addCounterLine,
  cancelCounterOrder,
  changeCounterLine,
  collectCounterOrder,
  createCounterOrder,
  findCustomerByPhone,
  getCounterOrder,
  listCounterOrders,
  lookupCounterOrder,
  removeCounterLine,
  submitCounterOrder,
  type CounterOrder,
  type CounterOrderLine,
  type CustomerMatch,
} from './api';
import { summariseLines } from './counterLines';

const { Text, Title } = Typography;

type Tab = 'new' | 'collect';

interface LineEdit {
  lineUid: string;
  change: () => Promise<CounterOrder>;
}

const SCANNER_STYLE = { fontSize: 20, height: 56 } as const;
const BIG_BUTTON_STYLE = { height: 52 } as const;

/**
 * The parts counter: scan to sell, scan the receipt to hand over.
 *
 * Keyboard-wedge first (CLAUDE.md §10), like the unit scanner: one
 * always-focused input per tab, a not-found result is written on the screen
 * rather than toasted, and focus returns to the scanner after every step so the
 * next part can be scanned with a gloved hand and no tapping.
 *
 * Unlike the unit scanner the input must NOT grab focus back from a quantity
 * field or an open drawer, so the blur handler only refocuses when focus went
 * nowhere in particular.
 */
export function CounterSalePage(): ReactNode {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>('new');

  return (
    <Flex vertical gap={16}>
      <Segmented<Tab>
        value={tab}
        onChange={setTab}
        size="large"
        block
        options={[
          { value: 'new', label: t('counter.tabNew') },
          { value: 'collect', label: t('counter.tabCollect') },
        ]}
      />
      {/* Both tabs stay mounted: switching must not strand an in-progress sale
          (its reserved stock stays held on the server). */}
      <div style={{ display: tab === 'new' ? undefined : 'none' }}>
        <NewSale active={tab === 'new'} />
      </div>
      <div style={{ display: tab === 'collect' ? undefined : 'none' }}>
        <Collect active={tab === 'collect'} />
      </div>
    </Flex>
  );
}

function errorText(e: unknown, fallback: string): string {
  return e instanceof ApiError ? e.message : fallback;
}

function NewSale({ active }: { active: boolean }): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const inputRef = useRef<InputRef>(null);

  const [code, setCode] = useState('');
  const [order, setOrder] = useState<CounterOrder | null>(null);
  const [customer, setCustomer] = useState<CustomerMatch | null>(null);
  const [notFound, setNotFound] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ text: string; warn: boolean } | null>(null);

  const [customerOpen, setCustomerOpen] = useState(false);
  const [phone, setPhone] = useState('');
  const [matches, setMatches] = useState<CustomerMatch[] | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [reason, setReason] = useState('');

  const focusInput = (): void => {
    inputRef.current?.focus();
  };
  useEffect(() => {
    if (active) focusInput();
  }, [active]);

  // Bumped per line when an edit is rejected, so the field remounts showing
  // the server's quantity instead of the rejected one.
  const [resets, setResets] = useState<Record<string, number>>({});
  const [locationUid, setLocationUid] = useState<string | undefined>(undefined);

  const drafts = useCursorList(['counter-orders', 'DRAFT'], (page) => listCounterOrders('DRAFT', page), {
    enabled: order === null,
  });
  const refreshDrafts = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['counter-orders', 'DRAFT'] });
  };

  const resume = useMutation({
    mutationFn: (uid: string) => getCounterOrder(uid),
    onSuccess: (resumed) => {
      setOrder(resumed);
      setCustomer(
        resumed.customerUid === null
          ? null
          : { uid: resumed.customerUid, name: resumed.customerName ?? '', phone: null },
      );
      setNotice(null);
      setNotFound(null);
      setProblem(null);
      focusInput();
    },
    onError: (e: unknown) => {
      setProblem(errorText(e, t('counter.actionFailed')));
      refreshDrafts();
    },
  });

  const reset = (): void => {
    setOrder(null);
    setCustomer(null);
    setLocationUid(undefined);
    setCode('');
    setNotFound(null);
    setProblem(null);
  };

  const scan = useMutation({
    mutationFn: async (scanned: string): Promise<CounterOrder> => {
      // The first scan opens the sale, so there is no "Start sale" button.
      let current = order;
      if (current === null) {
        current = await createCounterOrder({
          ...(customer === null ? {} : { customerUid: customer.uid }),
          ...(locationUid === undefined ? {} : { locationUid }),
        });
        setOrder(current);
        refreshDrafts();
      }
      return addCounterLine(current.uid, { code: scanned, qty: '1' });
    },
    onSuccess: (updated) => {
      setOrder(updated);
      setNotFound(null);
      setProblem(null);
      setNotice(null);
      setCode('');
      focusInput();
    },
    onError: (e: unknown, scanned: string) => {
      setNotice(null);
      setCode('');
      if (e instanceof ApiError && e.status === 404) {
        setNotFound(scanned);
        setProblem(null);
      } else {
        setNotFound(null);
        setProblem(errorText(e, t('counter.actionFailed')));
      }
      focusInput();
    },
  });

  const edit = useMutation({
    mutationFn: (edit: LineEdit) => edit.change(),
    onSuccess: (updated) => {
      setOrder(updated);
      setProblem(null);
      focusInput();
    },
    onError: (e: unknown, edit: LineEdit) => {
      setProblem(errorText(e, t('counter.actionFailed')));
      setResets((r) => ({ ...r, [edit.lineUid]: (r[edit.lineUid] ?? 0) + 1 }));
    },
  });

  const send = useMutation({
    mutationFn: () => submitCounterOrder(order?.uid ?? ''),
    onSuccess: (sent) => {
      const needsApproval = sent.status === 'PENDING_APPROVAL';
      setNotice({
        text: needsApproval ? t('counter.needsApproval') : t('counter.sent', { orderNo: sent.orderNo }),
        warn: needsApproval,
      });
      reset();
      refreshDrafts();
      focusInput();
    },
    onError: (e: unknown) => {
      setProblem(errorText(e, t('counter.actionFailed')));
      focusInput();
    },
  });

  const cancel = useMutation({
    mutationFn: () => cancelCounterOrder(order?.uid ?? '', reason.trim()),
    onSuccess: (cancelled) => {
      setNotice({ text: t('counter.cancelled', { orderNo: cancelled.orderNo }), warn: false });
      reset();
      refreshDrafts();
      setCancelOpen(false);
      setReason('');
      focusInput();
    },
    onError: (e: unknown) => {
      setProblem(errorText(e, t('counter.actionFailed')));
      setCancelOpen(false);
      focusInput();
    },
  });

  const findCustomer = useMutation({
    mutationFn: () => findCustomerByPhone(phone.trim()),
    onSuccess: (page) => {
      setMatches(page.items);
    },
    onError: (e: unknown) => {
      setProblem(errorText(e, t('counter.actionFailed')));
    },
  });

  const submitScan = (): void => {
    const value = code.trim();
    if (value.length === 0 || scan.isPending) return;
    scan.mutate(value);
  };

  const closeCustomer = (): void => {
    setCustomerOpen(false);
    setPhone('');
    setMatches(null);
    focusInput();
  };

  const lines = order?.lines ?? [];

  return (
    <Flex vertical gap={16}>
      <Card>
        <Flex vertical gap={12}>
          <Input
            ref={inputRef}
            className="tabular"
            style={SCANNER_STYLE}
            size="large"
            autoFocus
            autoComplete="off"
            inputMode="text"
            value={code}
            placeholder={t('counter.scanPlaceholder')}
            onChange={(e) => {
              setCode(e.target.value.toUpperCase());
            }}
            onBlur={(e) => {
              // Only take focus back when it went nowhere (a tap on empty
              // space). A quantity field or a drawer input keeps it.
              if (active && e.relatedTarget === null && !customerOpen && !cancelOpen) {
                window.setTimeout(focusInput, 0);
              }
            }}
            onPressEnter={submitScan}
          />
          <Flex align="center" justify="space-between" gap={8} wrap>
            <Text type="secondary">{customer?.name ?? t('counter.walkIn')}</Text>
            {/* The customer is fixed when the sale is created, so the picker is
                only offered before the first scan. */}
            {order === null && (
              <Button
                onClick={() => {
                  setCustomerOpen(true);
                }}
              >
                {t('counter.addCustomer')}
              </Button>
            )}
          </Flex>
          {/* Like the customer, the shelf is fixed when the first scan opens the sale. */}
          {order === null && (
            <StockLocationPicker value={locationUid} onChange={setLocationUid} label={t('stockLocation.sellFrom')} />
          )}
        </Flex>
      </Card>

      {notice !== null && <Alert type={notice.warn ? 'warning' : 'success'} showIcon message={notice.text} />}
      {notFound !== null && <Alert type="error" showIcon message={t('counter.notFound', { code: notFound })} />}
      {problem !== null && <Alert type="error" showIcon message={problem} />}

      {order === null && drafts.rows.length > 0 && (
        <Flex vertical gap={8}>
          <Text strong>{t('counter.openDrafts')}</Text>
          {drafts.rows.map((d) => (
            <Card key={d.uid} size="small">
              <Flex justify="space-between" align="center" gap={8}>
                <Flex vertical gap={2}>
                  <Text strong className="tabular">
                    {d.orderNo}
                  </Text>
                  <Text type="secondary">{d.customerName ?? t('counter.walkIn')}</Text>
                </Flex>
                <Flex align="center" gap={8}>
                  <Text className="tabular">{formatMoney(d.grandTotal)}</Text>
                  <Button
                    size="large"
                    loading={resume.isPending && resume.variables === d.uid}
                    onClick={() => {
                      resume.mutate(d.uid);
                    }}
                  >
                    {t('counter.resume')}
                  </Button>
                </Flex>
              </Flex>
            </Card>
          ))}
          <LoadMore list={drafts} />
        </Flex>
      )}

      {lines.length === 0 && (
        <Card>
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('counter.emptyNew')} />
        </Card>
      )}

      {lines.map((line) => (
        <LineRow
          key={`${line.uid}-${line.qty}-${String(resets[line.uid] ?? 0)}`}
          line={line}
          busy={edit.isPending}
          onQty={(qty) => {
            edit.mutate({ lineUid: line.uid, change: () => changeCounterLine(order?.uid ?? '', line.uid, { qty }) });
          }}
          onRemove={() => {
            edit.mutate({ lineUid: line.uid, change: () => removeCounterLine(order?.uid ?? '', line.uid) });
          }}
        />
      ))}

      {order !== null && lines.length > 0 && (
        <Card>
          <Flex justify="space-between" align="center">
            <Text>{t('counter.total')}</Text>
            <Title level={4} className="tabular" style={{ margin: 0 }}>
              {formatMoney(order.grandTotal)}
            </Title>
          </Flex>
        </Card>
      )}

      {order !== null && (
        <Flex vertical gap={8}>
          <Button
            type="primary"
            size="large"
            block
            style={BIG_BUTTON_STYLE}
            disabled={lines.length === 0}
            loading={send.isPending}
            onClick={() => {
              send.mutate();
            }}
          >
            {t('counter.send')}
          </Button>
          <Button
            size="large"
            block
            danger
            style={BIG_BUTTON_STYLE}
            onClick={() => {
              setCancelOpen(true);
            }}
          >
            {t('counter.cancel')}
          </Button>
        </Flex>
      )}

      <ActionDrawer
        open={customerOpen}
        title={t('counter.addCustomer')}
        submitLabel={t('counter.findCustomer')}
        submitting={findCustomer.isPending}
        submitDisabled={phone.trim().length < 3}
        onClose={closeCustomer}
        onSubmit={() => {
          findCustomer.mutate();
        }}
      >
        <Form layout="vertical">
          <Form.Item label={t('counter.customerPhone')}>
            <Input
              size="large"
              inputMode="tel"
              autoFocus
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value);
                setMatches(null);
              }}
              onPressEnter={() => {
                if (phone.trim().length >= 3) findCustomer.mutate();
              }}
            />
          </Form.Item>
        </Form>
        {matches !== null && matches.length === 0 && (
          <Alert type="info" showIcon message={t('counter.noCustomerFound')} />
        )}
        {(matches ?? []).map((m) => (
          <Button
            key={m.uid}
            block
            size="large"
            style={BIG_BUTTON_STYLE}
            onClick={() => {
              setCustomer(m);
              closeCustomer();
            }}
          >
            {m.name}
            {m.phone !== null ? ` · ${m.phone}` : ''}
          </Button>
        ))}
      </ActionDrawer>

      <ActionDrawer
        open={cancelOpen}
        title={t('counter.cancel')}
        submitLabel={t('counter.cancel')}
        danger
        submitting={cancel.isPending}
        submitDisabled={reason.trim().length < 3}
        onClose={() => {
          setCancelOpen(false);
          focusInput();
        }}
        onSubmit={() => {
          cancel.mutate();
        }}
        consequence={t('counter.cancelConsequence')}
      >
        <Form layout="vertical">
          <Form.Item label={t('counter.cancelReason')}>
            <Input.TextArea
              size="large"
              rows={3}
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
              }}
            />
          </Form.Item>
        </Form>
      </ActionDrawer>
    </Flex>
  );
}

function LineRow({
  line,
  busy,
  onQty,
  onRemove,
}: {
  line: CounterOrderLine;
  busy: boolean;
  onQty: (qty: string) => void;
  onRemove: () => void;
}): ReactNode {
  const { t } = useTranslation();
  return (
    <Card size="small">
      <Flex vertical gap={8}>
        <Flex justify="space-between" align="start" gap={8}>
          <Flex vertical gap={2}>
            <Text strong style={{ fontSize: 16 }}>
              {line.skuName}
            </Text>
            <Text type="secondary" className="tabular">
              {line.skuCode}
            </Text>
          </Flex>
          <Text className="tabular">{formatMoney(line.lineTotal)}</Text>
        </Flex>
        <Flex gap={8} align="center">
          <InputNumber
            aria-label={t('counter.quantity')}
            size="large"
            stringMode
            min="0.001"
            step="1"
            defaultValue={line.qty}
            disabled={busy}
            style={{ flex: 1 }}
            onBlur={(e) => {
              const typed = e.target.value.trim();
              if (typed.length > 0 && Number(typed) > 0 && Number(typed) !== Number(line.qty)) onQty(typed);
            }}
          />
          <Button
            size="large"
            danger
            icon={<DeleteOutlined />}
            aria-label={t('counter.remove')}
            style={{ height: 40, minWidth: 48 }}
            disabled={busy}
            onClick={onRemove}
          />
        </Flex>
      </Flex>
    </Card>
  );
}

function Collect({ active }: { active: boolean }): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const inputRef = useRef<InputRef>(null);

  const [code, setCode] = useState('');
  const [found, setFound] = useState<CounterOrder | null>(null);
  const [notFound, setNotFound] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const [handing, setHanding] = useState(false);
  // Generated ONCE when the drawer opens, like the till's payment key: a
  // retry after a short shelf must reuse it, or the guard guards nothing.
  const [key, setKey] = useState('');
  const [shortMessage, setShortMessage] = useState<string | null>(null);
  const [acknowledge, setAcknowledge] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [reason, setReason] = useState('');

  const focusInput = (): void => {
    inputRef.current?.focus();
  };
  useEffect(() => {
    if (active) {
      focusInput();
      void queryClient.invalidateQueries({ queryKey: ['counter-orders', 'READY_TO_COLLECT'] });
    }
  }, [active, queryClient]);

  const waiting = useCursorList(['counter-orders', 'READY_TO_COLLECT'], (page) =>
    listCounterOrders('READY_TO_COLLECT', page),
  );

  const lookup = useMutation({
    mutationFn: (scanned: string) => lookupCounterOrder(scanned),
    onSuccess: (order) => {
      setFound(order);
      setNotFound(null);
      setProblem(null);
      setDone(null);
      setCode('');
      focusInput();
    },
    onError: (e: unknown, scanned: string) => {
      setFound(null);
      setCode('');
      setDone(null);
      if (e instanceof ApiError && e.status === 404) {
        setNotFound(scanned);
        setProblem(null);
      } else {
        setNotFound(null);
        setProblem(errorText(e, t('counter.actionFailed')));
      }
      focusInput();
    },
  });

  const closeDrawer = (): void => {
    setHanding(false);
    setShortMessage(null);
    setAcknowledge(false);
    focusInput();
  };

  const collect = useMutation({
    mutationFn: () =>
      collectCounterOrder(found?.uid ?? '', acknowledge ? { acknowledgeNegative: true } : {}, key),
    onSuccess: (collected) => {
      void queryClient.invalidateQueries({ queryKey: ['counter-orders'] });
      setDone(t('counter.handedOver', { orderNo: collected.orderNo }));
      setFound(null);
      closeDrawer();
    },
    onError: (e: unknown) => {
      if (e instanceof ApiError && e.code === 'PARTS_INSUFFICIENT_STOCK') {
        setShortMessage(e.message);
      } else {
        setProblem(errorText(e, t('counter.actionFailed')));
        closeDrawer();
      }
    },
  });

  // A sent order the customer walked away from would otherwise hold its
  // reservation for good. Once billed it is the invoice void's job instead.
  const cancel = useMutation({
    mutationFn: () => cancelCounterOrder(found?.uid ?? '', reason.trim()),
    onSuccess: (cancelled) => {
      void queryClient.invalidateQueries({ queryKey: ['counter-orders'] });
      setDone(t('counter.cancelled', { orderNo: cancelled.orderNo }));
      setFound(null);
      setCancelOpen(false);
      setReason('');
      focusInput();
    },
    onError: (e: unknown) => {
      setProblem(errorText(e, t('counter.actionFailed')));
      setCancelOpen(false);
      focusInput();
    },
  });

  const submitScan = (): void => {
    const value = code.trim();
    if (value.length === 0 || lookup.isPending) return;
    lookup.mutate(value);
  };

  const ready = found?.status === 'READY_TO_COLLECT';
  const cancellable =
    found !== null &&
    found.invoiceUid === null &&
    (found.status === 'AWAITING_PAYMENT' || found.status === 'PENDING_APPROVAL');

  return (
    <Flex vertical gap={16}>
      <Card>
        <Input
          ref={inputRef}
          className="tabular"
          style={SCANNER_STYLE}
          size="large"
          autoFocus
          autoComplete="off"
          inputMode="text"
          value={code}
          placeholder={t('counter.scanReceipt')}
          onChange={(e) => {
            setCode(e.target.value.toUpperCase());
          }}
          onBlur={(e) => {
            if (active && e.relatedTarget === null && !handing && !cancelOpen) window.setTimeout(focusInput, 0);
          }}
          onPressEnter={submitScan}
        />
      </Card>

      {done !== null && <Alert type="success" showIcon message={done} />}
      {notFound !== null && <Alert type="error" showIcon message={t('counter.orderNotFound', { code: notFound })} />}
      {problem !== null && <Alert type="error" showIcon message={problem} />}

      {found !== null && (
        <Card
          title={
            <Flex vertical gap={2}>
              <Flex align="center" gap={8} wrap>
                <Text strong className="tabular" style={{ fontSize: 17 }}>
                  {found.orderNo}
                </Text>
                <Tag color={ready ? 'success' : undefined}>{t(`counter.status.${found.status}`)}</Tag>
              </Flex>
              <Text type="secondary">{found.customerName ?? t('counter.walkIn')}</Text>
            </Flex>
          }
        >
          <Flex vertical gap={12}>
            {found.lines.map((l) => (
              <Flex key={l.uid} justify="space-between" gap={8}>
                <Text>{l.skuName}</Text>
                <Text className="tabular">× {l.qty}</Text>
              </Flex>
            ))}
            {ready ? (
              <Button
                type="primary"
                size="large"
                block
                style={BIG_BUTTON_STYLE}
                onClick={() => {
                  setKey(crypto.randomUUID());
                  setShortMessage(null);
                  setAcknowledge(false);
                  setHanding(true);
                }}
              >
                {t('counter.handOver')}
              </Button>
            ) : (
              <Alert type="warning" showIcon message={t(`counter.notReady.${found.status}`)} />
            )}
            {cancellable && (
              <Button
                size="large"
                block
                danger
                style={BIG_BUTTON_STYLE}
                onClick={() => {
                  setCancelOpen(true);
                }}
              >
                {t('counter.cancel')}
              </Button>
            )}
          </Flex>
        </Card>
      )}

      {!waiting.isFetching && waiting.rows.length === 0 && found === null && (
        <Card>
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('counter.emptyCollect')} />
        </Card>
      )}

      {waiting.rows.map((o) => (
        <Card
          key={o.uid}
          size="small"
          hoverable
          onClick={() => {
            setFound(o);
            setNotFound(null);
            setProblem(null);
            setDone(null);
            focusInput();
          }}
        >
          <Flex justify="space-between" align="center" gap={8}>
            <Flex vertical gap={2}>
              <Text strong className="tabular">
                {o.orderNo}
              </Text>
              <Text type="secondary">{o.customerName ?? t('counter.walkIn')}</Text>
            </Flex>
            <Text className="tabular">{formatMoney(o.grandTotal)}</Text>
          </Flex>
        </Card>
      ))}
      <LoadMore list={waiting} />

      <ActionDrawer
        open={cancelOpen}
        title={t('counter.cancel')}
        submitLabel={t('counter.cancel')}
        danger
        submitting={cancel.isPending}
        submitDisabled={reason.trim().length < 3}
        onClose={() => {
          setCancelOpen(false);
          focusInput();
        }}
        onSubmit={() => {
          cancel.mutate();
        }}
        consequence={t('counter.cancelConsequence')}
      >
        <Form layout="vertical">
          <Form.Item label={t('counter.cancelReason')}>
            <Input.TextArea
              size="large"
              rows={3}
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
              }}
            />
          </Form.Item>
        </Form>
      </ActionDrawer>

      <ActionDrawer
        open={handing}
        title={t('counter.handOver')}
        submitLabel={t('counter.handOver')}
        submitting={collect.isPending}
        submitDisabled={shortMessage !== null && !acknowledge}
        onClose={closeDrawer}
        onSubmit={() => {
          collect.mutate();
        }}
        consequence={t('counter.handOverConsequence', { lines: summariseLines(found?.lines ?? []) })}
      >
        {shortMessage !== null && (
          <Alert
            type="warning"
            showIcon
            icon={<WarningFilled />}
            message={shortMessage}
            description={
              <Flex vertical gap={8}>
                <Text>{t('parts.shortHelp')}</Text>
                <Checkbox
                  checked={acknowledge}
                  onChange={(e) => {
                    setAcknowledge(e.target.checked);
                  }}
                >
                  {t('parts.acknowledge')}
                </Checkbox>
              </Flex>
            }
          />
        )}
      </ActionDrawer>
    </Flex>
  );
}
